import { Play } from "lucide-react";
import { motion } from "motion/react";
import { useSectionParallax } from "../../../hooks/useSectionParallax";

const STEPS = [
  { title: "Bring your data.", body: "CSV or Excel, including picking the sheet and header row. SAS Transport (.xpt) files are converted automatically." },
  { title: "Build the flow.", body: "Add blocks from the toolbar and wire them together. Each block has its own settings panel." },
  { title: "Run it.", body: "The whole flow executes, and every output block produces a chart, table, or report." },
  { title: "Save versions.", body: "Each save becomes a numbered version with its results kept. Work in progress is autosaved." },
  { title: "Export.", body: "One zip bundles charts as PNG, tables as CSV, and EDA reports as JSON." },
];

// Output blocks (Compare, charts, Model) are terminal in Flow, so the mock
// branches after Clean rather than chaining them.
const OUTPUT_NODES = [
  { kind: "Compare", name: "Groups side by side" },
  { kind: "Chart", name: "Box plot by group" },
  { kind: "Model", name: "Linear regression" },
];

function CanvasNode({ kind, name }: { kind: string; name: string }) {
  return (
    <div className="rounded-xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] px-3 py-2.5 text-left shadow-sm">
      <p className="text-[9px] font-semibold uppercase tracking-widest text-[#0284C7] dark:text-[#38BDF8]">{kind}</p>
      <p className="text-[11px] sm:text-xs font-medium text-[#0A0A0F] dark:text-white mt-0.5">{name}</p>
    </div>
  );
}

const CONNECTOR = "mx-auto w-px h-5 bg-[#0284C7]/40 dark:bg-[#38BDF8]/40";

function FlowCanvasMock() {
  return (
    <div className="rounded-2xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0A0A0F]/10 dark:border-white/10">
        <span className="text-xs font-medium text-[#0A0A0F] dark:text-white">Outcomes by group</span>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-[#5C5C66] dark:text-[#9A9A9A]">Saved · v3</span>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0284C7] dark:text-[#38BDF8]"><Play size={10} />Run</span>
        </div>
      </div>
      <div className="p-5 sm:p-8 bg-[radial-gradient(circle,rgba(10,10,15,0.08)_1px,transparent_1px)] dark:bg-[radial-gradient(circle,rgba(255,255,255,0.07)_1px,transparent_1px)] [background-size:16px_16px]">
        <div className="max-w-[200px] mx-auto"><CanvasNode kind="Source" name="dataset.xlsx" /></div>
        <div className={CONNECTOR} />
        <div className="max-w-[200px] mx-auto"><CanvasNode kind="Clean" name="Drop missing · Recode group" /></div>
        <div className={CONNECTOR} />
        <div className="mx-[16%] h-px bg-[#0284C7]/40 dark:bg-[#38BDF8]/40" />
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {OUTPUT_NODES.map((node) => (
            <div key={node.kind}>
              <div className={CONNECTOR} />
              <CanvasNode {...node} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FlowHowItWorks() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref} className="grid lg:grid-cols-2 gap-14 items-center">
      <motion.div style={{ y: fgY }}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0284C7] dark:text-[#38BDF8] mb-3">Flow · No Code</p>
        <h3 className="text-3xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white mb-4">Connect blocks. Press Run.</h3>
        <p className="text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A] mb-8">
          Flow is a visual, no-code canvas. Chain a data source through cleaning, transforms,
          statistics, and models to the charts and tables you need.
        </p>
        <ol className="space-y-4">
          {STEPS.map(({ title, body }, i) => (
            <li key={title} className="flex gap-4">
              <span className="font-mono text-xs text-[#0284C7] dark:text-[#38BDF8] pt-0.5">0{i + 1}</span>
              <p className="text-sm text-[#5C5C66] dark:text-[#9A9A9A]">
                <span className="font-medium text-[#0A0A0F] dark:text-white">{title}</span> {body}
              </p>
            </li>
          ))}
        </ol>
      </motion.div>
      <motion.div style={{ y: bgY }}>
        <FlowCanvasMock />
      </motion.div>
    </div>
  );
}
